/**
 * Program router — filters workbook-shaped rows by borrower answers.
 * Primary catalog is loaded from Supabase (loan_programs + thresholds + eligibility.source_row).
 * Falls back to bundled JSON when offline or before preload completes.
 */
import { supabase } from '../lib/supabase.js';
import {
  rowMatchesRouter,
  pickVariant,
  mapRowToProgramBase,
  routePriorityNum,
  groupByCode,
  getBundledRouterRows,
} from './programRouterLegacy.js';
import { evaluatePredicate } from './predicateEvaluator.js';

/** @type {Array<{ programId: string|null, source_row: object, thresholds: object|null, match_predicate: object|null }>|null} */
let catalogEntries = null;
let preloadPromise = null;

/** Reset cached catalog (tests / parity script only). */
export function resetProgramCatalogCache() {
  catalogEntries = null;
  preloadPromise = null;
}

function bool(v) {
  return String(v).toUpperCase() === 'TRUE';
}

/**
 * Fetch catalog from Supabase once; merge threshold overrides + optional match_predicate.
 * Safe to call multiple times — runs once.
 */
export async function preloadProgramCatalog() {
  if (preloadPromise) return preloadPromise;

  preloadPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('loan_programs')
        .select(`
          id,
          loan_program_thresholds (
            min_credit_score,
            max_ltv,
            max_dti,
            min_down_payment_pct
          ),
          loan_program_eligibility (
            source_row,
            match_predicate
          )
        `)
        .eq('is_active', true);

      if (error) throw error;

      const mapped = (data || [])
        .map((p) => {
          const elig = p.loan_program_eligibility;
          const sr = elig?.source_row;
          if (!sr || typeof sr !== 'object') return null;
          const th = p.loan_program_thresholds;
          return {
            programId: p.id,
            source_row: sr,
            thresholds: th && typeof th === 'object' ? th : null,
            match_predicate: elig?.match_predicate ?? null,
          };
        })
        .filter(Boolean);

      if (mapped.length > 0) {
        catalogEntries = mapped;
      } else {
        catalogEntries = buildBundledCatalogEntries();
      }
    } catch (e) {
      console.warn('[programRouter] Catalog fetch failed; using bundled JSON.', e);
      catalogEntries = buildBundledCatalogEntries();
    }
  })();

  return preloadPromise;
}

function buildBundledCatalogEntries() {
  return getBundledRouterRows().map((source_row) => ({
    programId: null,
    source_row,
    thresholds: null,
    match_predicate: null,
  }));
}

function ensureCatalogFallback() {
  if (!catalogEntries) {
    catalogEntries = buildBundledCatalogEntries();
  }
}

function getEntries() {
  ensureCatalogFallback();
  return catalogEntries;
}

function findEntryByRawCode(raw) {
  return getEntries().find((e) => e.source_row.rawProgramCode === raw) || null;
}

/** Rows used for filtering — one legacy workbook row per active program SKU. */
function getRouterRowsForMatching() {
  return getEntries().map((e) => e.source_row);
}

function rowMatchesWithPredicate(entry, answers) {
  const row = entry.source_row;
  if (!rowMatchesRouter(row, answers)) return false;
  if (entry.match_predicate != null) {
    return evaluatePredicate(entry.match_predicate, answers);
  }
  return true;
}

/**
 * Merge threshold overrides from DB into the base program object + Owen fields.
 */
export function mapRowToProgram(row) {
  const entry = findEntryByRawCode(row.rawProgramCode);
  const base = mapRowToProgramBase(row);

  if (entry?.thresholds) {
    const t = entry.thresholds;
    if (t.min_credit_score != null) base.minCreditScore = t.min_credit_score;
    if (t.max_ltv != null) base.maxLTV = Number(t.max_ltv);
    if (t.min_down_payment_pct != null) base.minDownPayment = Number(t.min_down_payment_pct);
    if (t.max_dti != null) base.maxDTI = Number(t.max_dti);
  }

  return {
    ...base,
    programId: entry?.programId ?? undefined,
    veteranEligibilityRequired: bool(row.veteranEligibilityRequired),
    USDAEligibleAreaRequired: bool(row.USDAEligibleAreaRequired),
    tribalEligibilityRequired: bool(row.tribalEligibilityRequired),
    primaryAllowed: bool(row.primaryAllowed),
    secondHomeAllowed: bool(row.secondHomeAllowed),
    investmentAllowed: bool(row.investmentAllowed),
    productAdherence: row.productAdherence || '',
    stateHFAParticipationRequired: bool(row.stateHFAParticipationRequired),
  };
}

export function matchProgramsFromRouter(answers) {
  const a = answers || {};
  const entries = getEntries();
  const matchedEntries = entries.filter((e) => rowMatchesWithPredicate(e, a));
  const matchedRows = matchedEntries.map((e) => e.source_row);

  const groups = groupByCode(matchedRows);
  const orderedCodes = [...groups.keys()].sort((c1, c2) => {
    const p1 = routePriorityNum(groups.get(c1)[0]);
    const p2 = routePriorityNum(groups.get(c2)[0]);
    return p1 - p2;
  });

  const picks = [];
  for (const code of orderedCodes) {
    if (picks.length >= 8) break;
    const variant = pickVariant(groups.get(code), a);
    if (variant) picks.push(mapRowToProgram(variant));
  }

  const top = picks.slice(0, 3);
  if (top.length > 0) return top;

  const rows = getBundledRouterRows();
  const fallback = rows.filter(
    (r) =>
      r.routerProgramCode === 'CONV_STD_CONFORMING' &&
      r.rateType === 'Fixed' &&
      parseInt(r.amortizationTermYears, 10) === 30
  );
  const fb = pickVariant(fallback, a);
  return fb ? [mapRowToProgram(fb)] : [];
}

export function getRouterRowByRawCode(rawProgramCode) {
  const rows = getRouterRowsForMatching();
  return rows.find((r) => r.rawProgramCode === rawProgramCode) || null;
}

export function getDistinctRouterProgramCount() {
  const rows = getRouterRowsForMatching();
  return new Set(rows.map((r) => r.routerProgramCode)).size;
}

export function getProgramsByCategory(category) {
  const rows = getRouterRowsForMatching();
  const codes = new Set(
    rows.filter((r) => r.productFamily === category).map((r) => r.routerProgramCode)
  );
  return [...codes];
}
